import { Sidebar } from '@hssoft/design-system-shared-lib';
import type {
  SidebarFooterVersion,
  SidebarItem as SidebarItemShape,
} from '@hssoft/design-system-shared-lib';
import { FC, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router-dom';
import { map } from 'rxjs';
import appVersion from '../../../app-version.json';
import { LogoIcon } from '../../../svg/custom.svg.tsx';
import { navigateService } from '../../services/navigate/navigate.service.ts';
import { navBlocker } from './blocker/nav.blocker.ts';
import { navService } from './nav.service.ts';
import { promptPopup } from './prompt-popup.ts';

const BUILT_AT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

function formatBuiltAt(builtAt: string, locale: string): string {
  const date = new Date(builtAt);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString(locale, BUILT_AT_DATE_OPTIONS);
  } catch {
    return date.toLocaleDateString('en', BUILT_AT_DATE_OPTIONS);
  }
}

interface IAppSidebar {
  children: ReactNode;
}

export const AppSidebar: FC<IAppSidebar> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const { pathname } = useLocation();
  // While a router transition is in flight, `useNavigation().location` holds the destination
  // pathname (before loaders resolve). Prefer it so the active item updates the instant a link
  // is clicked, instead of waiting ~2s for the route's loader to finish.
  const { location: pendingLocation } = useNavigation();

  // Optimistic href: set synchronously in handleItemClick so it batches with setOpen(false)
  // in the same React render. Without this, the sidebar closes (open=false) one render before
  // pendingLocation resolves, leaving a frame where the clicked item is isActive=false + open=false
  // — which triggers the collapsed hover CSS and causes a light-grey → white → grey flash.
  const [clickedHref, setClickedHref] = useState<string | null>(null);
  useEffect(() => {
    setClickedHref(null);
  }, [pathname]);

  const activeHref = clickedHref ?? pendingLocation?.pathname ?? pathname;

  // Bridge to the legacy navService so other call-sites (globalSearch, orderList, etc.) keep
  // working unchanged: external openMenu/closeMenu/navigateTo events drive the controlled state.
  useEffect(() => {
    const navigationToSub = navService.sub.navigateTo().subscribe((to) => {
      navigateService.pub.navigateTo(to);
    });
    const menuOpenSub = navService.sub
      .openMenu()
      .pipe(map(({ state }) => state))
      .subscribe(setOpen);
    const menuCloseSub = navService.sub.closeMenu().subscribe(setOpen);
    return () => {
      navigationToSub.unsubscribe();
      menuOpenSub.unsubscribe();
      menuCloseSub.unsubscribe();
      promptPopup.stream.unsubscribe();
    };
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (next) navService.pub.openMenu(true);
    else navService.pub.closeMenu();
  };

  const handleItemClick = (item: SidebarItemShape, event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (!item.href) return;
    navBlocker.controlledNavigate(() => {
      setClickedHref(item.href!);
      navService.pub.navigateTo(item.href!);
    });
  };

  const footerVersion = useMemo<SidebarFooterVersion>(
    () => ({
      versionText: t('common.version'),
      versionValue: appVersion.version,
      description: formatBuiltAt(appVersion.builtAt, i18n.language),
    }),
    [t, i18n.language],
  );

  return (
    <>
      <Sidebar
        variant='permanent'
        floating
        open={open}
        onOpenChange={handleOpenChange}
        activeHref={activeHref}
        onItemClick={handleItemClick}
        brand={{
          logo: <LogoIcon sx={{ width: 34, height: 34 }} />,
          title: <Sidebar.BrandTitle bold='WaWi' light='Assist' />,
        }}
        expandedWidth={280}
        ariaLabel='Primary navigation'
        footerVersion={footerVersion}
      >
        {children}
      </Sidebar>
      <promptPopup.Component />
    </>
  );
};
